import { useCallback, useEffect, useRef, useState } from 'react';
import { getDocumentLinks } from '@/api/inquiries';
import { getConversation } from '@/api/messages';
import { normalizeDocumentIds } from '@/api/normalize';
import { ACTIVE_RUN_STATUSES } from '@/api/runs';
import type { DocumentLink } from '@/utils/messageParser';
import { runStore, type RunState } from '../runs/runStore';
import type { ConvMessage } from '../types';

/**
 * The messages of the open Q&A, oldest first (GET /chat/message/{id}
 * without a cursor, so questions and answers can be numbered). Loaded again
 * when one of its runs ends. After loading, a run the list says is still
 * active is picked up from the server (reload reconciliation,
 * chat-runs-api.md §10).
 */

/** Questions known only in this tab, per Q&A (kept across Q&A switches). */
const localMessages = new Map<string, ConvMessage[]>();

const byId = (a: ConvMessage, b: ConvMessage) => Number(a.id) - Number(b.id);

/** Server messages plus the local ones the server does not list. */
export const mergeMessages = (
  server: ConvMessage[],
  local: ConvMessage[],
): ConvMessage[] => {
  const ids = new Set(server.map(message => String(message.id)));
  const kept = local.filter(
    message => message.local === 'removed' || !ids.has(String(message.id)),
  );
  return [...server, ...kept].sort(byId);
};

/** Question/answer numbers: position among the messages the server keeps. */
export const numberMessages = (messages: ConvMessage[]) => {
  const numbers = new Map<string, number>();
  let n = 0;
  for (const message of messages) {
    if (message.local === 'removed') continue;
    numbers.set(String(message.id), ++n);
  }
  return numbers;
};

/**
 * Pick up the newest run the list says is active, or settle a lost one.
 * `onEnded` runs when a listed-active run turns out to have ended already
 * (the list is then stale).
 */
const reconcileRuns = (
  inquiryId: string,
  messages: ConvMessage[],
  onEnded: () => void,
) => {
  for (const message of [...messages].reverse()) {
    const runId = message.run_id;
    if (!runId || message.local) continue;
    const known = runStore.getRun(runId);
    const listedActive = ACTIVE_RUN_STATUSES.has(String(message.run_status));
    if (listedActive && !known) {
      void runStore.attach(runId, inquiryId).then(details => {
        if (details && !ACTIVE_RUN_STATUSES.has(details.status)) onEnded();
      });
      return;
    }
    if (
      !listedActive &&
      known &&
      (known.phase === 'disconnected' || known.phase === 'interrupted')
    ) {
      void runStore.reconnect(runId);
    }
    if (listedActive) return;
  }
};

export const useConversation = (inquiryId: string | undefined) => {
  const [messages, setMessages] = useState<ConvMessage[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [links, setLinks] = useState<DocumentLink[]>([]);
  const inquiryRef = useRef(inquiryId);
  inquiryRef.current = inquiryId;
  const messagesRef = useRef<ConvMessage[]>([]);
  const requestRef = useRef(0);
  const linksRef = useRef(links);
  linksRef.current = links;

  const showLocal = useCallback((id: string) => {
    setMessages(previous =>
      mergeMessages(
        previous.filter(message => !message.local),
        localMessages.get(id) ?? [],
      ),
    );
  }, []);

  /** Links of the documents old answers cite (legacy reference markers). */
  const loadLegacyLinks = useCallback(
    async (id: string, list: ConvMessage[]) => {
      const known = new Set(linksRef.current.map(link => link.id));
      const ids = [
        ...new Set(
          list
            .filter(message => message.format !== 'html')
            .flatMap(message => normalizeDocumentIds(message.document_ids)),
        ),
      ].filter(documentId => !known.has(documentId));
      if (ids.length === 0) return;
      const response = await getDocumentLinks(id, ids);
      if (!response.success || inquiryRef.current !== id) return;
      setLinks(previous => {
        const have = new Set(previous.map(link => link.id));
        return [
          ...previous,
          ...(response.data as DocumentLink[]).filter(
            link => !have.has(link.id),
          ),
        ];
      });
    },
    [],
  );

  const load = useCallback(
    async (id: string) => {
      const request = ++requestRef.current;
      const response = await getConversation(id);
      if (request !== requestRef.current || inquiryRef.current !== id) return;
      if (!response.success) {
        setFailed(true);
        setLoadedFor(id);
        return;
      }
      const server = [...(response.data as ConvMessage[])].sort(byId);
      const serverIds = new Set(server.map(message => String(message.id)));
      // Pending questions the list now has are no longer needed.
      const local = (localMessages.get(id) ?? []).filter(
        message =>
          message.local === 'removed' || !serverIds.has(String(message.id)),
      );
      localMessages.set(id, local);
      setFailed(false);
      setMessages(mergeMessages(server, local));
      setLoadedFor(id);
      reconcileRuns(id, server, () => {
        if (inquiryRef.current === id) void load(id);
      });
      void loadLegacyLinks(id, server);
    },
    [loadLegacyLinks],
  );

  useEffect(() => {
    setMessages([]);
    setLoadedFor(null);
    setFailed(false);
    setLinks([]);
    if (inquiryId) void load(inquiryId);
  }, [inquiryId, load]);

  // A run of this Q&A ended: show the stored answer. A rejected run's
  // question was removed by the server; keep it here with its failure.
  useEffect(
    () =>
      runStore.onFinished((run: RunState) => {
        // Only a rejected run has its question removed by the server.
        if (
          run.phase === 'failed' &&
          run.messageId === null &&
          run.error?.code === 'rejected'
        ) {
          const local = localMessages.get(run.inquiryId) ?? [];
          // A run picked up after a reload has no local copy: keep the
          // question as listed, so it does not vanish without its reason.
          const shown = messagesRef.current.find(
            message => message.run_id === run.runId,
          );
          const copies =
            local.some(message => message.run_id === run.runId) || !shown
              ? local
              : [...local, shown];
          localMessages.set(
            run.inquiryId,
            copies.map(message =>
              message.run_id === run.runId
                ? { ...message, local: 'removed', run_status: 'failed' }
                : message,
            ),
          );
        }
        if (run.inquiryId === inquiryRef.current) {
          showLocal(run.inquiryId);
          void load(run.inquiryId);
        }
      }),
    [load, showLocal],
  );

  /** Show a question that was just sent, until the list is loaded again. */
  const addPending = useCallback(
    (id: string, message: ConvMessage) => {
      const local = (localMessages.get(id) ?? []).filter(
        item => String(item.id) !== String(message.id),
      );
      localMessages.set(id, [...local, { ...message, local: 'pending' }]);
      if (inquiryRef.current === id) showLocal(id);
    },
    [showLocal],
  );

  const reload = useCallback(
    () => (inquiryRef.current ? load(inquiryRef.current) : Promise.resolve()),
    [load],
  );

  messagesRef.current = messages;

  // Back to the tab: a listed-active run whose pick-up failed is tried
  // again (the list is loaded again, which picks it up).
  useEffect(() => {
    const onFocus = () => {
      const id = inquiryRef.current;
      if (
        id &&
        messagesRef.current.some(
          message =>
            !!message.run_id &&
            ACTIVE_RUN_STATUSES.has(String(message.run_status)) &&
            !runStore.getRun(message.run_id),
        )
      ) {
        void load(id);
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  return {
    messages,
    /** The list of the open Q&A has been loaded (or failed to load). */
    loaded: !!inquiryId && loadedFor === inquiryId,
    failed,
    links,
    reload,
    addPending,
  };
};
